import React from 'react';
import {
  MapPin, Calendar, Clock, DollarSign, Briefcase, FileText, Database, Shield, Gift
} from 'lucide-react';
import { TooltipContext } from './TooltipContext';
import TimedPill from './TimedPill';
import PillList from './PillList';
import InfoRow from './InfoRow';

const dash = '–';

const ListingFront = ({ profile = {}, isCertified = false, showClinicalInfo = false }) => {
  const [openId, setOpenId] = React.useState(null);
  const facility = profile.facility || {};
  const { facilityType = dash, location } = facility;
  const locationLabel = location ? location.split(',')[0] : dash;
  const displayFacilityType = profile.facilityType || facilityType;
  const displayLocation = profile.location || locationLabel;
  let rateRange;
  if (profile.payAmount) {
    if (profile.payPeriod === 'annual') {
      rateRange = `${Math.round(profile.payAmount / 1000)}K/yr`;
    } else {
      rateRange = `${profile.payAmount}/hr`;
    }
  } else {
    rateRange = profile.compensation?.rateRange ?? dash;
  }
  // Contract type lookup order: compensation.contractType > contractType > compensation.w2Or1099 > dash
  const contractType = profile.compensation?.contractType || profile.contractType || (profile.compensation?.w2Or1099 ?? dash);
  
  // Debug contract type data
  console.log('[ListingFront] Contract type data:', {
    fromCompensationContractType: profile.compensation?.contractType,
    fromContractType: profile.contractType,
    fromCompensationW2Or1099: profile.compensation?.w2Or1099,
    finalValue: contractType
  });
  // Ensure assignment type is always an array
  const assignmentType = Array.isArray(profile.assignmentType) ? profile.assignmentType : 
                        (profile.assignment?.types ? profile.assignment.types : 
                        (profile.assignment?.type ? [profile.assignment.type] : []));
  
  // Format start date - if within 3 weeks, show as "Immediate Start"
  let startDateValue = profile.startDate || (profile.coverageNeeds?.startDate ?? dash);
  if (startDateValue !== dash) {
    try {
      const startDateObj = new Date(startDateValue);
      const today = new Date();
      const threeWeeksFromNow = new Date();
      threeWeeksFromNow.setDate(today.getDate() + 21);
      
      if (startDateObj <= threeWeeksFromNow) {
        startDateValue = "Immediate Start";
      }
    } catch (e) {
      // If date parsing fails, just use the original value
      console.log("Error parsing date:", e);
    }
  }
  
  // Schedule type with multiple fallbacks (flat -> nested -> coverageNeeds)
  // Using correct field name 'pattern' for schedule
  const scheduleType = profile.scheduleType || 
                      (profile.schedule?.pattern) || 
                      (profile.coverageNeeds?.scheduleType ?? dash);
                      
  // Schedule boolean properties with multiple fallbacks (nested -> flat)
  const onCall = profile.schedule?.onCall || profile.onCall || false;
  const weekends = profile.schedule?.weekends || profile.weekends || false;
  
  // Debug schedule type data
  console.log('[ListingFront] Schedule type data:', {
    fromFlatField: profile.scheduleType,
    fromSchedulePattern: profile.schedule?.pattern,
    fromCoverageNeeds: profile.coverageNeeds?.scheduleType,
    finalValue: scheduleType
  });
  const estimatedWeeklyHours = profile.estimatedWeeklyHours || (profile.coverageNeeds?.estimatedWeeklyHours ?? dash);
  
  // Sign-on bonus information
  const hasSignOnBonus = profile.signOnBonus?.offered || profile.compensation?.signOnBonusOffered || false;
  const signOnBonusAmount = profile.signOnBonus?.amount || profile.compensation?.signOnBonusAmount || 0;
  const signOnBonusCommitment = profile.signOnBonus?.commitment || profile.compensation?.signOnBonusCommitmentYears || 0;
  
  // Debug sign-on bonus data
  console.log('[ListingFront] Sign-on bonus data:', {
    hasBonus: hasSignOnBonus,
    amount: signOnBonusAmount,
    commitment: signOnBonusCommitment,
    fromSignOnBonus: {
      offered: profile.signOnBonus?.offered,
      amount: profile.signOnBonus?.amount,
      commitment: profile.signOnBonus?.commitment
    },
    fromCompensation: {
      offered: profile.compensation?.signOnBonusOffered,
      amount: profile.compensation?.signOnBonusAmount,
      commitmentYears: profile.compensation?.signOnBonusCommitmentYears
    }
  });
  
  const signOnBonusDisplay = hasSignOnBonus 
    ? `$${signOnBonusAmount.toLocaleString()}${signOnBonusCommitment ? ` (${signOnBonusCommitment}yr)` : ''}`
    : "No Bonus";
  
  // Get clinical info for when we need to show it on the front
  const requiredCaseTypes = (profile.clinicalExpectations?.requiredCaseTypes?.length
    ? profile.clinicalExpectations.requiredCaseTypes
    : [dash]);
  const emrSystem = profile.clinicalExpectations?.emrSystem ?? dash;

  return (
    <TooltipContext.Provider value={{ openId, setOpenId }}>
      <div className="card-face-content listing-gradient-bg" role="region" aria-label="Listing Front">
        <div className="card-header">
          <h2 className="card-title">{displayFacilityType}</h2>
          {isCertified && (
            <div className="cert-badge">
              <Shield size={18} className="cert-icon" />
            </div>
          )}
        </div>

        <InfoRow Icon={MapPin}>
          <PillList
            items={[displayLocation]}
            getTooltip={() => 'Facility location'}
            idPrefix="location"
          />
        </InfoRow>
        
        <InfoRow Icon={DollarSign}>
          <div className="pay-contract-details">
            <TimedPill
              id="pay-rate"
              label={rateRange}
              tooltipText="Compensation range for this position"
            />
            <PillList
              items={[contractType]}
              getTooltip={() => 'W2 = employed; 1099 = contract'}
              idPrefix="contract"
            />
          </div>
        </InfoRow>
        
        {/* New sign-on bonus information */}
        <InfoRow Icon={Gift}>
          <TimedPill
            id="sign-on-bonus"
            label={signOnBonusDisplay}
            tooltipText={hasSignOnBonus 
              ? `Sign-on bonus of $${signOnBonusAmount.toLocaleString()}${signOnBonusCommitment ? ` with ${signOnBonusCommitment} year commitment` : ''}`
              : "No sign-on bonus offered"
            }
          />
        </InfoRow>
        
        <InfoRow Icon={Calendar}>
          <TimedPill
            id="start-date"
            label={startDateValue}
            tooltipText="Start date for this assignment"
          />
        </InfoRow>
        
        <InfoRow Icon={Clock}>
          <div className="schedule-details">
            <TimedPill
              id="schedule-type"
              label={scheduleType}
              tooltipText="Type of schedule (e.g., day, night, rotating)"
            />
            <TimedPill
              id="estimated-weekly-hours"
              label={estimatedWeeklyHours}
              tooltipText="Estimated weekly hours for this assignment"
            />
            {onCall && (
              <TimedPill
                id="on-call-required"
                label="On-Call"
                tooltipText="On-call is required for this position"
              />
            )}
            {weekends && (
              <TimedPill
                id="weekends-required"
                label="Weekends"
                tooltipText="Weekend shifts are required for this position"
              />
            )}
          </div>
        </InfoRow>
        
        <InfoRow Icon={Briefcase}>
          <PillList
            items={assignmentType}
            getTooltip={type => `Assignment type: ${type}`}
            idPrefix="assignment"
          />
        </InfoRow>
        
        {showClinicalInfo && (
          <>
            <InfoRow Icon={FileText}>
              <div className="required-case-types-wrapper">
                <PillList
                  items={requiredCaseTypes}
                  getTooltip={type => `Required case type: ${type}`}
                  idPrefix="case-type"
                />
              </div>
            </InfoRow>

            <InfoRow Icon={Database}>
              <PillList
                items={[emrSystem]}
                getTooltip={() => 'EMR system used by this facility'}
                idPrefix="emr"
              />
            </InfoRow>
          </>
        )}
      </div>
    </TooltipContext.Provider>
  );
};

export default ListingFront;
